import React from 'react';
import {
  DialogContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AppDialog, { AppDialogHeader } from '../common/AppDialog';
import {
  People as PeopleIcon,
} from '@mui/icons-material';
import { User, CreateUserRequest, UpdateUserRequest } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import UserForm from './UserForm';

interface UserDialogProps {
  open: boolean;
  user?: User | null;
  onClose: () => void;
  onSubmit: (data: CreateUserRequest | UpdateUserRequest) => Promise<void>;
  isLoading?: boolean;
}

const UserDialog: React.FC<UserDialogProps> = ({
  open,
  user,
  onClose,
  onSubmit,
  isLoading = false,
}) => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const titleId = React.useId();
  const handleClose = () => { if (!isLoading) onClose(); };
  
  const handleSubmit = async (data: CreateUserRequest | UpdateUserRequest) => {
    await onSubmit(data);
    onClose();
  };

  return (
    <AppDialog presentation="form" contentPadding="none" closeDisabled={isLoading}
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      scroll="paper"
      aria-labelledby={titleId}
      PaperProps={{
        sx: { width: isMobile ? '100%' : 620, maxHeight: isMobile ? '100%' : 'min(92vh, 760px)', borderRadius: isMobile ? 0 : 3, overflow: 'hidden' }
      }}
    >
      <AppDialogHeader id={titleId} icon={<PeopleIcon />} subtitle={user ? `${user.firstName} ${user.lastName}`.trim() : undefined}>
        {user ? t('users.editUser') : t('users.createUser')}
      </AppDialogHeader>

      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', flex: isMobile ? 1 : undefined }}>
        <UserForm
          user={user}
          onSubmit={handleSubmit}
          onCancel={handleClose}
          isLoading={isLoading}
        />
      </DialogContent>
    </AppDialog>
  );
};

export default UserDialog;
